import { useEffect, useState } from "react";
import { Download, X, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const PAGE = 50;
const eur = (v) => `\u20ac${Number(v || 0).toFixed(2)}`;

function downloadCsv(pie, slice, year, entries) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const ev = entries.some(e => e.event);
  const rows = [["Date", "Description", ...(ev ? ["Event"] : []), "Person", "Amount (EUR)"], ...entries.map(e => [e.date, e.label, ...(ev ? [e.event] : []), e.person, e.amount.toFixed(2)])];
  const blob = new Blob([rows.map(r => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${pie}-${slice.replace(/[^a-z0-9]+/gi, "_")}${year ? `-${year}` : ""}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function PieSliceDetails({ pie, slice, year, color, onClose, testid }) {
  const { authAxios } = useAuth();
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    setData(null); setLimit(PAGE);
    authAxios.get("/fa-analytics/finance/details", { params: { pie, slice, year } })
      .then(r => setData(r.data)).catch(() => setData({ entries: [], count: 0, total: 0, error: true }));
  }, [pie, slice, year]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`${testid}-details`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />
        <p className="flex-1 min-w-0 text-sm font-bold text-[#1e3a5f] break-words leading-tight">{slice}</p>
        {data && <span className="text-xs text-slate-500 whitespace-nowrap" data-testid={`${testid}-details-summary`}>{data.count} entries · {eur(data.total)}</span>}
        <button onClick={() => downloadCsv(pie, slice, year, data?.entries || [])} disabled={!data?.count}
          className="px-2.5 py-1 rounded-full border text-xs font-medium text-[#1e3a5f] hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
          data-testid={`${testid}-details-download`}><Download className="w-3 h-3" /> CSV</button>
        <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100 text-slate-400" data-testid={`${testid}-details-close`}><X className="w-4 h-4" /></button>
      </div>
      {!data && <div className="py-4 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
      {data?.error && <p className="text-xs text-red-500">Could not load the entries.</p>}
      {data && !data.error && data.count === 0 && <p className="text-xs text-slate-400">No entries.</p>}
      {data?.count > 0 && (() => { const showEvent = data.entries.some(e => e.event); return (
        <div>
          <ul className="sm:hidden divide-y divide-slate-50" data-testid={`${testid}-details-list`}>
            {data.entries.slice(0, limit).map((e, i) => (
              <li key={`${e.date}-${e.label}-${e.person}-${i}`} className="py-1.5 text-xs">
                <div className="flex justify-between gap-2">
                  <span className="min-w-0 break-words text-slate-700">{e.label || "\u2014"}</span>
                  <span className="font-semibold text-slate-700 tabular-nums whitespace-nowrap">{eur(e.amount)}</span>
                </div>
                <p className="text-[11px] text-slate-400 break-words">{[e.date, showEvent && e.event, e.person].filter(Boolean).join(" · ")}</p>
              </li>
            ))}
          </ul>
          <table className="hidden sm:table w-full text-xs" data-testid={`${testid}-details-table`}>
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-1.5 pr-2 font-medium">Date</th><th className="pr-2 font-medium">Description</th>
                {showEvent && <th className="pr-2 font-medium">Event</th>}<th className="pr-2 font-medium">Person</th><th className="text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.slice(0, limit).map((e, i) => (
                <tr key={`${e.date}-${e.label}-${e.person}-${i}`} className="border-b border-slate-50 align-top">
                  <td className="py-1.5 pr-2 whitespace-nowrap text-slate-500">{e.date || "\u2014"}</td>
                  <td className="pr-2 text-slate-700 break-words">{e.label || "\u2014"}</td>
                  {showEvent && <td className="pr-2 text-slate-500 break-words">{e.event || "\u2014"}</td>}
                  <td className="pr-2 text-slate-500 break-words">{e.person || "\u2014"}</td>
                  <td className="text-right font-semibold text-slate-700 tabular-nums whitespace-nowrap">{eur(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.count > limit && (
            <button onClick={() => setLimit(l => l + PAGE)} className="mt-2 w-full py-1.5 rounded-lg bg-slate-50 text-xs text-[#1e3a5f] font-medium hover:bg-slate-100" data-testid={`${testid}-details-more`}>
              Show more ({data.count - limit} left)
            </button>
          )}
        </div>
      ); })()}
    </div>
  );
}
